import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

export interface BloodDripOverlayProps {
  /** Flip to true to run the drip. The overlay animates for `durationMs`,
   *  calls `onComplete`, then fades itself out — reset `active` in
   *  `onComplete`; the fade-out is handled internally. */
  active: boolean;
  /** Full drip run before `onComplete` fires. Default 5000ms. */
  durationMs?: number;
  /** Fires once when the drip finishes (or immediately under
   *  prefers-reduced-motion, where the animation is skipped entirely). */
  onComplete?: () => void;
}

interface Drip {
  x: number;
  y: number;
  w: number;
  /** px per ms at multiplier 1. */
  v: number;
  /** ms before this streak starts running. */
  delay: number;
  wobble: number;
  /** 0..1 color variance — some streaks darker, some fresher. */
  shade: number;
}

/** Full-screen theatrical overlay: blood pools along the top edge and streaks
 *  drip to the bottom of the viewport, then the whole thing fades away.
 *  Blocks pointer input while running (it's a dramatic pause, not a spinner).
 *  Used by Proposals to make saving an "Absolutely Not" margin feel like the
 *  crime it is. Honors prefers-reduced-motion by skipping straight to
 *  `onComplete`. */
export function BloodDripOverlay({ active, durationMs = 5000, onComplete }: BloodDripOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [phase, setPhase] = useState<"idle" | "dripping" | "fading">("idle");

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onCompleteRef.current?.();
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    setPhase("dripping");

    const w = window.innerWidth;
    const h = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const drips: Drip[] = [];
    for (let x = 6; x < w; x += 9 + Math.random() * 26) {
      const delay = Math.random() * 1400;
      drips.push({
        x: x + Math.random() * 6,
        y: 0,
        w: 2.5 + Math.random() * 5,
        v: h / ((durationMs - delay) * (0.85 + Math.random() * 0.6)),
        delay,
        wobble: Math.random() * Math.PI * 2,
        shade: Math.random(),
      });
    }
    // A few guaranteed runners so streaks visibly hit the bottom in time.
    for (let i = 0; i < 6; i++) {
      const d = drips[Math.floor(((i + 0.5) * drips.length) / 6)];
      if (!d) continue;
      d.delay = Math.random() * 350;
      d.v = h / ((durationMs - d.delay) * 0.72);
    }

    let raf = 0;
    let start = 0;
    let last = 0;
    let finished = false;
    let fadeTimer = 0;

    const finish = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(failsafeTimer);
      setPhase("fading");
      onCompleteRef.current?.();
      fadeTimer = window.setTimeout(() => setPhase("idle"), 900);
    };
    // rAF pauses in hidden tabs; the timeout doesn't. Guarantees the gated
    // save still fires on time even if the user switches tabs mid-drip.
    const failsafeTimer = window.setTimeout(finish, durationMs + 300);

    const tick = (t: number) => {
      if (!start) {
        start = t;
        last = t;
      }
      const dt = t - last;
      last = t;
      const elapsed = t - start;

      // Pooling band along the top — thickens over the first second.
      const bandH = Math.min(34, 6 + (elapsed / 800) * 28);
      // Blood is content, not theme — same dark red in light and dark mode.
      ctx.fillStyle = "rgb(109, 4, 5)";
      ctx.fillRect(0, 0, w, bandH);

      for (const d of drips) {
        if (elapsed < d.delay || d.y >= h + 8) continue;
        // Speed pulses (surface tension) — multiplier stays positive.
        const m = 0.85 + 0.35 * Math.sin(elapsed / 260 + d.wobble);
        const ny = Math.min(d.y + d.v * dt * m, h + 8);
        const r = Math.floor(90 + d.shade * 60);
        ctx.fillStyle = `rgb(${r}, ${Math.floor(4 + d.shade * 10)}, ${Math.floor(6 + d.shade * 8)})`;
        const wob = Math.sin(d.y / 48 + d.wobble) * 1.4;
        // Trail segment (canvas is never cleared, so trails persist)…
        ctx.fillRect(d.x + wob - d.w / 2, Math.max(d.y - 1, 0), d.w, ny - d.y + 2);
        // …with a rounded droplet head.
        ctx.beginPath();
        ctx.ellipse(d.x + wob, ny, d.w * 0.75, d.w * 1.25, 0, 0, Math.PI * 2);
        ctx.fill();
        d.y = ny;
      }

      if (elapsed >= durationMs) {
        finish();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      // Let a finished run fade out on its own; only interrupt a live one.
      if (!finished) {
        window.clearTimeout(failsafeTimer);
        window.clearTimeout(fadeTimer);
        setPhase("idle");
      }
    };
  }, [active, durationMs]);

  if (!active && phase === "idle") return null;
  return createPortal(
    <canvas
      ref={canvasRef}
      aria-hidden
      // z-[100] sits above Radix dialogs (z-50); input is blocked only while
      // the drip is actually running.
      className={cn(
        "fixed inset-0 z-[100] transition-opacity duration-700",
        phase === "dripping" ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
      )}
    />,
    document.body,
  );
}
